import { useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { useFilters } from '@atlassian/jira-business-filters/src/controllers/index.tsx';
import { useQuickFilters } from '@atlassian/jira-business-filters/src/controllers/quick-filters/index.tsx';
import { useHierarchyStore } from '@atlassian/jira-business-list/src/controllers/hierarchy/hierarchy-store/index.tsx';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { useGroupBy } from '../../group-by';
import { useBulkSelectionStore } from '../bulk-selection-store';
import { getAllIssuesIds } from '../utils';

export const useWatchBulkSelection = () => {
	const [{ selectedIssues }, { clearSelection, unselectIssues }] = useBulkSelectionStore();
	const projectData = useProject();
	const projectId = projectData.id;
	const { jql } = useFilters();
	const { quickFiltersJql } = useQuickFilters();
	const [
		{
			hierarchyData: { data: issues },
			childrenItems,
		},
	] = useHierarchyStore();
	const [groupByFieldId] = useGroupBy();
	const isGrouping = groupByFieldId != null;

	/*
        Use state to check once the filters has been updated
        After this we should wait for the new issues to be fetched
        and unselect the issues that are no longer visible for this new query
    */
	const [hasFiltersBeenUpdated, setHasFiltersBeenUpdated] = useState(false);
	const prevIssues = usePreviousWithInitial(issues);

	// clear all items when grouping & filters change as the groups reset and collapse
	useEffect(() => {
		if (selectedIssues.size && (jql || quickFiltersJql)) {
			if (isGrouping) {
				clearSelection();
			} else {
				setHasFiltersBeenUpdated(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jql, quickFiltersJql]);

	// clear items which are no longer visible after filtering etc.
	useEffect(() => {
		if (hasFiltersBeenUpdated && !isEqual(prevIssues, issues) && !isGrouping) {
			const allIssueIds = getAllIssuesIds({ data: issues, childrenItems });
			const issuesToUnselect = [...selectedIssues].filter(
				(issueId) => allIssueIds.indexOf(issueId) === -1,
			);
			if (issuesToUnselect.length > 0) {
				unselectIssues({ issues: issuesToUnselect });
			}
			setHasFiltersBeenUpdated(false);
		}
	}, [
		childrenItems,
		hasFiltersBeenUpdated,
		isGrouping,
		issues,
		prevIssues,
		selectedIssues,
		unselectIssues,
	]);

	// clear all items when changing project
	useEffect(() => {
		clearSelection();
	}, [projectId, clearSelection]);
};
